import { useEffect, useMemo, useState } from "react";
import "./css/3way_zommed_table.css";
import Swal from "sweetalert2";
import { apiRequest } from "../api";
import { createPortal } from "react-dom";

function isEmptyData(data) {
  if (data === null || data === undefined) {
    return true;
  }
  if (Array.isArray(data)) {
    return data.length === 0;
  }
  if (typeof data === "object") {
    return Object.keys(data).length === 0;
  }
  return false;
}

function deepClone(data) {
  if (data === null || data === undefined) {
    return data;
  }
  return JSON.parse(JSON.stringify(data));
}


function sanitizeNonStandardJson(raw) {
  let result = "";
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const char = raw[i];

    if (inString) {
      result += char;

      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }

      continue;
    }

    if (char === '"') {
      inString = true;
      result += char;
      continue;
    }

    // Replace non-standard numeric tokens only outside strings.
    const remaining = raw.slice(i);
    const match = remaining.match(/^-Infinity|^(?:NaN|Infinity)\b/);

    if (match) {
      const token = match[0];
      const previous = i > 0 ? raw[i - 1] : "";
      const next = raw[i + token.length] ?? "";

      // Ensure the token is a complete JSON value, not part of a word.
      const validBefore =
        i === 0 || /[\s:[,{]/.test(previous);

      const validAfter =
        next === "" || /[\s,\]}]/.test(next);

      if (validBefore && validAfter) {
        result += "null";
        i += token.length - 1;
        continue;
      }
    }

    result += char;
  }

  return result;
}

function parseRawData(rawData) {
  let value = rawData;

  // Parse repeatedly because the outer data and nested data
  // may each be JSON-encoded strings.
  for (let i = 0; i < 5 && typeof value === "string"; i++) {
    const input = sanitizeNonStandardJson(value);

    try {
      value = JSON.parse(input);
    } catch (error) {
      console.error("Unable to parse invoice-check data:", error);
      return null;
    }
  }

  return value;
}


function isNumeric(val) {
  return !Number.isNaN(Number(val)) && val !== null && val !== "";
}

function formatIndianInteger(value) {
  if (value === null || value === undefined) {
    return "";
  }

  const normalized = String(value).replace(/,/g, "");
  if (Number.isNaN(Number(normalized))) {
    return String(value);
  }

  const integer = String(Math.floor(Number(normalized)));
  const lastThree = integer.slice(-3);
  const other = integer.slice(0, -3);

  if (!other) {
    return lastThree;
  }

  return `${other.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${lastThree}`;
}

function beautifyLabel(label) {
  if (!label) {
    return "";
  }

  const tokenOverrides = {
    grn: "GRN",
    ocr: "OCR",
    gst: "GST",
    gstr: "GSTR",
    "3b": "3B",
    of: "of",
    gstn: "GSTN",
    per: "per",
    on: "on",
    to: "to",
    as: "as",
    gl: "GL",
    migo: "MIGO",
    pan: "PAN",
    "206ab": "206AB",
    "(month1)": "(Month1)",
    "(month2)": "(Month2)",
    "(month3)": "(Month3)",
  };

  return String(label)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const token = word.toLowerCase();
      if (tokenOverrides[token]) {
        return tokenOverrides[token];
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function getDisplayLabel(label, key) {
  const beautified = beautifyLabel(label);
  const normalized = beautified.trim().toLowerCase();

  if (
    key === "r4" &&
    (normalized === "vendor gstr 3b filing status" ||
      normalized === "vendor gstr 1 filing status")
  ) {
    return <span className="invoice-check-modal__label-strong">{beautified}</span>;
  }

  return beautified;
}

function getGroupedHeaderCells(headers) {
  const hasUnitColumns =
    headers.includes("unit") && headers.includes("GRN_unit");

  const hasRemarkColumns =
    headers.includes("processor_remark") && headers.includes("checker_remark");

  const invoiceSpan = hasUnitColumns ? 4 : 3;
  const migoSpan = hasUnitColumns ? 5 : 4;

  const resultSpan = hasRemarkColumns ? 7 : 5;

  const remaining = headers.length - invoiceSpan - migoSpan;

  const groups = [
    { label: "Invoice", span: invoiceSpan },
    { label: "MIGO", span: migoSpan },
  ];

  if (remaining >= resultSpan) {
    groups.push(
      { label: "PO", span: 2 },
      { label: "Result", span: remaining - 2 }
    );
  } else {
    groups.push({
      label: "Result",
      span: Math.max(remaining, 1),
    });
  }

  return groups;
}

function getHeadersFromArray(data) {
  const headerSet = new Set();
  data.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (String(key).toLowerCase() !== "color") {
        headerSet.add(key);
      }
    });
  });
  return Array.from(headerSet);
}

function getHeadersFromObject(data) {
  const headerSet = new Set();
  Object.values(data || {}).forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (String(key).toLowerCase() !== "color") {
        headerSet.add(key);
      }
    });
  });
  return Array.from(headerSet);
}

function isNestedObjectData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }

  const firstValue = Object.values(data)[0];
  return Boolean(firstValue) && typeof firstValue === "object" && !Array.isArray(firstValue);
}



function getCellDisplayValue({ key, field, value }) {
  if (field === "processor_remark") {
    return value ?? "";
  }

  if (key !== "r6" && key !== "r7" && isNumeric(value)) {
    return formatIndianInteger(value);
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}



function SectionTable({
  data,
  keyName,
  sectionKey,
  remarks,
  onRemarkChange,
  disagreements,
  onDisagreeChange,
  scoringOptions,
  r1Rows,
  onR1DragStart,
  onR1DragOver,
  onR1Drop,
  onR1Break,
  onR1Zoom,
  disposedRecordIds,
  onR1Action,
  isZoomed,
}) {
  
  
  if (Array.isArray(data)) {
    const headers = getHeadersFromArray(data).filter(
      (header) => header !== "record_id"
    );
    const isR1 = keyName === "r1";
    const rows = isR1 && Array.isArray(r1Rows) ? r1Rows : data;

    return (
      <div className="invoice-check-modal__table-wrap">
        {isR1 ? (
          <div className="invoice-check-modal__r1-tools">
            <div className="invoice-check-modal__help">
              <span>🖱️ <strong>Join:</strong> Drag &amp; Drop</span>
              <span className="invoice-check-modal__separator">•</span>
              <span>✂️ <strong>Break:</strong> Double-click</span>
            </div>
            {!isZoomed && (
              <button className="ghost-button" type="button" onClick={onR1Zoom}>
                Zoom
              </button>
            )}
          </div>
        ) : null}

        <table className={`invoice-check-modal__table ${isR1 ? "invoice-check-modal__table--r1" : ""}`}>
          <thead>
            {isR1 ? (
              <tr>
                {getGroupedHeaderCells(headers).map((group) => (
                  <th key={`${sectionKey || "default"}-${group.label}`} colSpan={group.span}>
                    {group.label}
                  </th>
                ))}
              </tr>
            ) : null}
            <tr>
              {headers.map((header) => (
                <th key={header}>{beautifyLabel(header)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const rowIndex = row.__rowIndex ?? index;
              const draggable = row.__draggable ?? row.status === "Unmatched_df2";

              const isTemporaryRecord = typeof row.record_id === "string" && row.record_id.startsWith("temp_");

              const hasRecordId = row.record_id !== null && row.record_id !== undefined && row.record_id !== "";

              const droppable = row.__droppable ?? (!hasRecordId || isTemporaryRecord);

              const breakable = row.__breakable ?? (hasRecordId && !isTemporaryRecord);

              const hidden = row.__hidden === true;
              const isDisposed =isR1 && row.record_id !== null && row.record_id !== undefined && disposedRecordIds.includes(row.record_id);

              if (hidden) {
                return null;
              }

              return (
                <tr
                  key={String(rowIndex)}
                  className={[
                    draggable ? "invoice-check-modal__row--draggable" : "",
                    droppable ? "invoice-check-modal__row--droppable" : "",
                    row.__assigned ? "invoice-check-modal__row--assigned" : "",
                    row.__dragOver ? "invoice-check-modal__row--drag-over" : "",
                    isDisposed ? "invoice-check-modal__row--disposed" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  draggable={isR1 ? draggable : undefined}
                  onDragStart={isR1 ? (event) => onR1DragStart(event, rowIndex) : undefined}
                  onDragOver={isR1 && droppable ? (event) => onR1DragOver(event, rowIndex) : undefined}
                  onDragLeave={isR1 ? () => onR1DragOver(null, null) : undefined}
                  onDrop={isR1 && droppable ? (event) => onR1Drop(event, rowIndex) : undefined}
                  onDoubleClick={isR1 && breakable ? () => onR1Break(rowIndex) : undefined}
                  
                >
                  {headers.map((header) => (
                    <td key={`${rowIndex}-${header}`}>
                      {header === "action" ? (
                        row.action === "set_unallocated" ? (
                          <div className="invoice-check-modal__action-dropdown">
                            <button
                              type="button"
                              className="invoice-check-modal__action-button"
                              onClick={() =>
                                onR1Action(
                                  "set_unallocated",
                                  row.record_id,
                                  sectionKey,
                                  rowIndex,
                                  row.item_quantity,
                                  row.unit_price,
                                )
                              }
                            >
                              Set Unallocated
                            </button>

                            <button
                              type="button"
                              className="invoice-check-modal__action-button invoice-check-modal__action-button--dispose"
                              onClick={() =>
                                onR1Action(
                                  "dispose",
                                  row.record_id,
                                  sectionKey,
                                  rowIndex,
                                  row.item_quantity,
                                  row.unit_price,
                                )
                              }
                            >
                              Dispose
                            </button>
                          </div>
                        ) : row.action === "dispose" ? (
                          <button
                            type="button"
                            className="invoice-check-modal__action-button invoice-check-modal__action-button--dispose"
                            onClick={() =>
                              onR1Action(
                                "dispose",
                                row.record_id,
                                sectionKey,
                                rowIndex,
                                row.item_quantity,
                                row.unit_price,
                              )
                            }
                          >
                            Dispose
                          </button>
                        ) : null
                      ) : header === "processor_remark" ? (
                        <input
                          className="invoice-check-modal__remark-input"
                          type="text"
                          maxLength={50}
                          value={
                            remarks[
                              `${sectionKey || "default"}::${rowIndex}::${header}`
                            ] ?? row[header] ?? ""
                          }
                          onChange={(event) =>
                            onRemarkChange(
                              sectionKey,
                              rowIndex,
                              header,
                              event.target.value
                            )
                          }
                          placeholder="Enter remark..."
                        />
                      ) : header === "Reason" ? (
                        <div className="reason-tooltip">
                          <span className="reason-badge">View</span>
                          <div className="reason-tooltip__content">
                            {row[header]}
                          </div>
                        </div>
                      ) : header === "item_description" ||
                        header === "GRN_description" ? (
                        <div
                          className="description-cell"
                          title={row[header] ?? ""}
                        >
                          {row[header]}
                        </div>
                      ) : (
                        getCellDisplayValue({
                          key: keyName,
                          field: header,
                          value: row[header],
                        })
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (isNestedObjectData(data)) {
    const headers = getHeadersFromObject(data);

    const hasDisagreementRemark = Object.entries(disagreements).some(
      ([compositeKey, value]) => {
        const [disagreementSectionKey] = compositeKey.split("::");

        return (
          disagreementSectionKey === (sectionKey || "default") &&
          value === true
        );
      }
    );

    const displayHeaders = headers.includes("processor_remark")
      ? headers
      : hasDisagreementRemark
        ? [...headers, "processor_remark"]
        : headers;

    return (
      <div className="invoice-check-modal__table-wrap">
        <table className="invoice-check-modal__table">
          <thead>
            <tr>
              <th>Parameter</th>

              {displayHeaders.map((header) => (
                <th key={header}>{beautifyLabel(header)}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Object.entries(data).map(([rowId, row]) => {
              const disagreementKey =
                `${sectionKey || "default"}::${rowId}`;

              const isDisagreed =
                disagreements[disagreementKey] ??
                row?.disagree ??
                false;

              // Get dropdown options for this specific parameter
              const options =
                scoringOptions?.[sectionKey]?.[rowId] || [];

              return (
                <tr key={rowId}>
                  <td>{getDisplayLabel(rowId, keyName)}</td>

                  {displayHeaders.map((header) => (
                    <td key={`${rowId}-${header}`}>

                      {/* DISAGREE */}
                      {header === "disagree" ? (
                        <button
                          type="button"
                          className={`invoice-check-modal__disagree-button ${
                            isDisagreed
                              ? "invoice-check-modal__disagree-button--revert"
                              : ""
                          }`}
                          onClick={() =>
                            onDisagreeChange(
                              sectionKey,
                              rowId,
                              !isDisagreed
                            )
                          }
                        >
                          {isDisagreed ? "Revert" : "Disagree"}
                        </button>

                      /* PROCESSOR REMARK */
                      ) : header === "processor_remark" ? (
                        isDisagreed ? (
                          <span
                            className="invoice-check-modal__explanation"
                            title={
                              remarks[
                                `${sectionKey || "default"}::${rowId}::processor_remark`
                              ] ??
                              row?.processor_remark ??
                              "No explanation provided."
                            }
                          >
                            Explanation
                          </span>
                        ) : (
                          <input
                            className="invoice-check-modal__remark-input"
                            type="text"
                            maxLength={50}
                            value={
                              remarks[
                                `${sectionKey || "default"}::${rowId}::processor_remark`
                              ] ??
                              row?.processor_remark ??
                              ""
                            }
                            onChange={(event) =>
                              onRemarkChange(
                                sectionKey,
                                rowId,
                                "processor_remark",
                                event.target.value
                              )
                            }
                            placeholder="Enter remark..."
                          />
                        )


                      /* NORMAL FIELDS */
                      ) : (
                        getCellDisplayValue({
                          key: keyName,
                          field: header,
                          value: row?.[header],
                        })
                      )}

                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (data && typeof data === "object") {
    const headers = Object.keys(data).filter(
      (header) => String(header).toLowerCase() !== "color"
    );

    const hasDisagreement = Object.entries(disagreements).some(
      ([compositeKey, value]) => {
        const [disagreementSectionKey] = compositeKey.split("::");

        return (
          disagreementSectionKey === (sectionKey || "default") &&
          value === true
        );
      }
    );

    const displayHeaders = headers.includes("processor_remark")
      ? headers
      : hasDisagreement
        ? [...headers, "processor_remark"]
        : headers;

    return (
      <div className="invoice-check-modal__table-wrap">
        <table className="invoice-check-modal__table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Status/Value</th>
            </tr>
          </thead>

          <tbody>
            {displayHeaders.map((header) => {
              const disagreementKey =
                `${sectionKey || "default"}::flat`;

              const isDisagreed =
                disagreements[disagreementKey] ??
                data?.disagree ??
                false;

              // For flat data, header itself is the parameter name
              const options =
                scoringOptions?.[sectionKey]?.[header] || [];

              return (
                <tr key={header}>
                  <td>{getDisplayLabel(header, keyName)}</td>

                  <td>
                    {/* DISAGREE */}
                    {header === "disagree" ? (
                      <button
                        type="button"
                        className={`invoice-check-modal__disagree-button ${
                          isDisagreed
                            ? "invoice-check-modal__disagree-button--revert"
                            : ""
                        }`}
                        onClick={() =>
                          onDisagreeChange(
                            sectionKey,
                            "flat",
                            !isDisagreed
                          )
                        }
                      >
                        {isDisagreed ? "Revert" : "Disagree"}
                      </button>

                    /* PROCESSOR REMARK */
                    ) : header === "processor_remark" ? (
                      isDisagreed ? (
                        <select
                          className="invoice-check-modal__remark-select"
                          value={
                            remarks[
                              `${sectionKey || "default"}::flat::processor_remark`
                            ] ??
                            data?.processor_remark ??
                            ""
                          }
                          onChange={(event) =>
                            onRemarkChange(
                              sectionKey,
                              "flat",
                              "processor_remark",
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            Select reason...
                          </option>

                          {options.map((option, index) => (
                            <option
                              key={`flat-option-${index}`}
                              value={option}
                            >
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="invoice-check-modal__no-disagreement">
                          —
                        </span>
                      )

                    /* NORMAL FIELD */
                    ) : (
                      getCellDisplayValue({
                        key: keyName,
                        field: header,
                        value: data[header],
                      })
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return <p className="empty-state">Unsupported data format.</p>;
}

function buildUpdatedSectionData(
  sectionData,
  remarks,
  disagreements,
  sectionKey
) {
  const cloned = deepClone(sectionData);
  const sectionToken = sectionKey || "default";

  // --------------------------------------------------
  // 1. Apply processor remarks
  // --------------------------------------------------
  Object.entries(remarks).forEach(([compositeKey, value]) => {
    const [remarkSectionKey, rowId, field] = compositeKey.split("::");

    if (
      remarkSectionKey !== sectionToken ||
      field !== "processor_remark"
    ) {
      return;
    }

    if (!value.trim()) {
      return;
    }

    // Array - kept for existing functionality
    if (Array.isArray(cloned)) {
      const index = Number(rowId);

      if (Number.isInteger(index) && cloned[index]) {
        cloned[index][field] = value.trim();
      }

      return;
    }

    // Nested object
    if (isNestedObjectData(cloned)) {
      if (
        cloned[rowId] &&
        typeof cloned[rowId] === "object"
      ) {
        cloned[rowId][field] = value.trim();
      }

      return;
    }

    // Flat object
    if (
      cloned &&
      typeof cloned === "object" &&
      rowId === "flat"
    ) {
      cloned[field] = value.trim();
    }
  });

  // --------------------------------------------------
  // 2. Apply disagree / revert state
  // --------------------------------------------------
  Object.entries(disagreements).forEach(
    ([compositeKey, value]) => {
      const [disagreementSectionKey, rowId] =
        compositeKey.split("::");

      if (disagreementSectionKey !== sectionToken) {
        return;
      }

      // IMPORTANT:
      // R1 is handled separately by buildR1Payload().
      // We don't need disagreement handling here for arrays.
      if (Array.isArray(cloned)) {
        return;
      }

      // Nested object
      if (isNestedObjectData(cloned)) {
        if (
          cloned[rowId] &&
          typeof cloned[rowId] === "object"
        ) {
          cloned[rowId].disagree = Boolean(value);
        }

        return;
      }

      // Flat object
      if (
        cloned &&
        typeof cloned === "object" &&
        rowId === "flat"
      ) {
        cloned.disagree = Boolean(value);
      }
    }
  );

  return cloned;
}

function normalizeR1Rows(data) {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((row, index) => ({
    ...deepClone(row),
    __rowIndex: index,
    __draggable: row?.status === "Unmatched_df2",
    __droppable: row?.index_matching === null || row?.index_matching === "",
    __breakable: row?.index_matching !== null && row?.index_matching !== "",
    __hidden: false,
    __assigned: false,
    __dragOver: false,
  }));
}

function buildR1Payload(rows) {
  return rows
    .filter((row) => !row.__hidden)
    .map((row) => {
      const cleaned = {};
      Object.entries(row).forEach(([key, value]) => {
        if (!key.startsWith("__")) {
          cleaned[key] = value === "" ? null : value;
        }
      });
      return cleaned;
    });
}

export default function InvoiceCheckDetailModal({ detail, invoiceRow, scoringOptions, onClose, onSaved }) {
  
  const parsedData = useMemo(() => parseRawData(detail?.data), [detail]);
  const isMultiSection = useMemo(
    () => ["r1", "r2", "r5", "r6"].includes(detail?.key) && parsedData && typeof parsedData === "object" && !Array.isArray(parsedData),
    [detail?.key, parsedData],
  );
  const [remarks, setRemarks] = useState({});
  const [r1RowsBySection, setR1RowsBySection] = useState({});
  const [dragSource, setDragSource] = useState(null);
  const [savingSection, setSavingSection] = useState("");
  const [error, setError] = useState("");
  const [zoomedSection, setZoomedSection] = useState("");
  const [disposeRecordIds, setDisposeRecordIds] = useState([]);
  const [showUnallocatedModal, setShowUnallocatedModal] = useState(false);
  const [unplannedForm, setUnplannedForm] = useState(null);
  const [unplannedValues, setUnplannedValues] = useState({});
  const [unallocatedRecordId, setUnallocatedRecordId] = useState(null);
  const [unallocatedCostData, setUnallocatedCostData] = useState(null);
  const [lineItemvalue, setLineItemValue] = useState(null);
  const [unallocatedRowIndex, setUnallocatedRowIndex] = useState(null);
  const [disagreements, setDisagreements] = useState({});

  useEffect(() => {
    setRemarks({});
    setDragSource(null);
    setSavingSection("");
    setError("");

    if (detail?.key === "r1" && parsedData && typeof parsedData === "object" && !Array.isArray(parsedData)) {
      const nextState = {};
      Object.entries(parsedData).forEach(([sectionKey, sectionValue]) => {
        const sectionRows = Array.isArray(sectionValue?.data) ? sectionValue.data : sectionValue;
        nextState[sectionKey] = normalizeR1Rows(sectionRows);
      });
      setR1RowsBySection(nextState);
      return;
    }

    setR1RowsBySection({});
  }, [detail, parsedData]);

  useEffect(() => {
    if (!detail) {
      return undefined;
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [detail, onClose]);

  if (!detail) {
    return null;
  }

  

  async function loadUnplannedCostForm(materialService) {
    try {
      const payload = await apiRequest(
        "/invoice/unplanned-cost-form/",
        {
          method: "POST",
          body: {
            invoice_keys: invoiceRow.invoiceGroupKey,
            material_service: materialService,
          },
        }
      );

      setUnplannedForm(payload);

      const defaults = {};

      Object.values(payload.normal_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      Object.values(payload.grn_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      Object.values(payload.po_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      defaults.amount = "";

      setUnplannedValues(defaults);

      return payload;

    } catch (error) {
      console.error("Unable to load unallocated cost form:", error);

      await Swal.fire({
        icon: "error",
        title: "Unable to Open Unallocated Cost",
        text: error.message || "Unable to load unallocated cost form.",
      });

      return null;
    }
  }

  const handleR1Action = async (
    action,
    recordId,
    sectionKey,
    rowIndex,
    rowitem_quantity,
    rowitem_rate,
    
  ) => {
    console.log("R1 Action:", action);
    console.log("Record ID:", recordId);
    console.log("sectionKey:", sectionKey);

    if (action === "dispose") {
      if (
        recordId === null ||
        recordId === undefined ||
        recordId === ""
      ) {
        return;
      }

      setDisposeRecordIds((prev) =>
        prev.includes(recordId)
          ? prev
          : [...prev, recordId]
      );

      return;
    }

    if (action === "set_unallocated") {
      

      /*
      * Check the invoice-vs-MIGO rule first.
      *
      * We need to use the section3 values that are already
      * available on this page.
      */
      const invoiceValue = Number(invoiceRow?.invoiceValue || 0);
      const invoiceValueMigo = Number(invoiceRow?.invoiceMigoValue || 0);
      const LineItemvalue = Number(rowitem_quantity || 0)*Number(rowitem_rate || 0);
      setLineItemValue(LineItemvalue)
      if (invoiceValue <= invoiceValueMigo) {
        const result = await Swal.fire({
          icon: "warning",
          title: "Cannot Create Unallocated Cost",
          text:
            "Invoice Value is equal to or less than MIGO Value. " +
            "Unallocated Cost cannot be created.",
          showCancelButton: true,
          confirmButtonText: "Reject Invoice",
          cancelButtonText: "Proceed",
        });

        /*
        * User selected Reject Invoice
        */
        if (result.isConfirmed) {
          // Put your existing reject-invoice logic here.
          // Example:
          // await rejectInvoice();

          return;
        }

        /*
        * User selected Proceed.
        *
        * Continue to unallocated cost form.
        */
      }

      /*
      * Load the dynamic unallocated-cost form.
      */
      const materialService =
        detail?.material_service ||
        invoiceRow.material_service ||
        "material";

      const payload = await loadUnplannedCostForm(materialService);

      if (!payload) {
        return;
      }

      /*
      * Remember which row this form belongs to.
      */
      setUnallocatedRecordId(recordId);
      setUnallocatedRowIndex(rowIndex);

      /*
      * Open popup.
      */
      setShowUnallocatedModal(true);
    }
  };

  function updateUnplannedField(field, value) {
    setUnplannedValues((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function handleUnallocatedCostSave() {
    const temporaryRecordId =
    r1RowsBySection["2/3 Way Match"]?.[unallocatedRowIndex]?.record_id;

    console.log("Saving unallocated cost");
    console.log("Selected row index:", unallocatedRowIndex);
    console.log("Form values:", unplannedValues);

    // ---------------------------------------------
    // 1. Save complete form data separately
    // ---------------------------------------------
    setUnallocatedCostData({
      ...unplannedValues,
      record_id: temporaryRecordId,
    });

    // ---------------------------------------------
    // 2. Update ONLY the selected R1 table row
    // ---------------------------------------------
    setR1RowsBySection((current) => ({
      ...current,

      "2/3 Way Match": (
        current["2/3 Way Match"] || []
      ).map((item, index) => {

        // Don't touch other rows
        if (index !== unallocatedRowIndex) {
          return item;
        }

        // Update selected row
        return {
          ...item,

          record_id: temporaryRecordId,

          GRN_description:
            unplannedValues.item_descritption || "Unallocated Cost",

          GRN_qty: 1,

          GRN_rate: Number(unplannedValues.amount || 0),

          status: "Manually Matched (Unallocated Cost)",

          Reason: "Manually matched using Unallocated Cost",

          action: null,
        };
      }),
    }));

    // ---------------------------------------------
    // 3. Store temporary ID
    // ---------------------------------------------
    setUnallocatedRecordId(temporaryRecordId);

    // ---------------------------------------------
    // 4. Close popup
    // ---------------------------------------------
    setShowUnallocatedModal(false);

    // Optional cleanup
    setUnallocatedRowIndex(null);
  }
  


  function handleRemarkChange(sectionKey, rowId, field, value) {
    setRemarks((current) => ({
      ...current,
      [`${sectionKey || "default"}::${rowId}::${field}`]: value,
    }));
  }

  function handleDisagreeChange(sectionKey, rowId, value) {
    setDisagreements((current) => ({
      ...current,
      [`${sectionKey || "default"}::${rowId}`]: value,
    }));
  }

  function updateR1Section(sectionKey, updater) {
    setR1RowsBySection((current) => ({
      ...current,
      [sectionKey]: updater(current[sectionKey] || []),
    }));
  }

  function handleR1DragStart(event, rowIndex) {
    setDragSource(rowIndex);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(rowIndex));
  }

  function handleR1DragOver(sectionKey, targetRowIndex) {
    updateR1Section(sectionKey, (rows) =>
      rows.map((row) => ({
        ...row,
        __dragOver: row.__rowIndex === targetRowIndex,
      })),
    );
  }

  function handleR1Drop(event, sectionKey, targetRowIndex) {
    event.preventDefault();
    const sourceRowIndex = dragSource ?? event.dataTransfer.getData("text/plain");

    updateR1Section(sectionKey, (rows) => {
      const sourceRow = rows.find((row) => String(row.__rowIndex) === String(sourceRowIndex));
      const targetRow = rows.find((row) => String(row.__rowIndex) === String(targetRowIndex));

      if (!sourceRow || !targetRow) {
        return rows.map((row) => ({ ...row, __dragOver: false }));
      }

      if (targetRow.__assigned) {
        setError("This invoice row is already manually matched.");
        return rows.map((row) => ({ ...row, __dragOver: false }));
      }

      setError("");

      const grnColumns = ["GRN_description", "GRN_qty", "GRN_rate"];
      const poColumns = ["po_qty", "po_rate"];
      const matchingIndex = String(sourceRow.index_matching ?? "").trim();

      return rows.map((row) => {
        if (String(row.__rowIndex) === String(targetRowIndex)) {
          const updated = {
            ...row,
            index_matching: matchingIndex,
            status: "Manually Matched",
            Reason: "Manually matched by user",
            __assigned: true,
            __droppable: false,
            __breakable: true,
            __dragOver: false,
          };

          [...grnColumns, ...poColumns].forEach((column) => {
            updated[column] = sourceRow[column] ?? "";
          });

          return updated;
        }

        if (String(row.__rowIndex) === String(sourceRowIndex)) {
          return {
            ...row,
            __hidden: true,
            __dragOver: false,
          };
        }

        return {
          ...row,
          __dragOver: false,
        };
      });
    });

    setDragSource(null);
  }

  function handleR1Break(sectionKey, rowIndex) {
    updateR1Section(sectionKey, (rows) => {
      const targetRow = rows.find((row) => String(row.__rowIndex) === String(rowIndex));
      if (!targetRow) {
        return rows;
      }

      const headers = Object.keys(targetRow).filter((key) => !key.startsWith("__"));
      const originalMatchingIndex = targetRow.index_matching;
      const grnColumns = ["GRN_description", "GRN_qty", "GRN_rate", "po_qty", "po_rate"];
      const timestampIndex = `new_${Date.now()}`;

      const splitRow = {};
      headers.forEach((header) => {
        splitRow[header] = grnColumns.includes(header) ? targetRow[header] ?? "-" : "-";
      });

      splitRow.index_matching = originalMatchingIndex;
      splitRow.status = "Unmatched_df2";
      splitRow.Reason = "Match broken by user";

      return rows.flatMap((row) => {
        if (String(row.__rowIndex) !== String(rowIndex)) {
          return [
            {
              ...row,
              __dragOver: false,
            },
          ];
        }

        const invoiceOnlyRow = {
          ...row,
          GRN_description: "",
          GRN_qty: "",
          GRN_rate: "",
          po_qty: "",
          po_rate: "",
          index_matching: "",
          status: "Unmatched",
          Reason: "Match broken by user",
          __droppable: true,
          __breakable: false,
          __assigned: false,
          __dragOver: false,
        };

        const newMigoRow = {
          ...splitRow,
          __rowIndex: timestampIndex,
          __draggable: true,
          __droppable: false,
          __breakable: false,
          __hidden: false,
          __assigned: false,
          __dragOver: false,
        };

        return [invoiceOnlyRow, newMigoRow];
      });
    });
  }

  async function handleSave(sectionKey, sectionData) {
    setSavingSection(sectionKey || "default");
    setError("");

    try {
      let updatedData;

      // R1 remains completely unchanged
      if (
        detail.key === "r1" &&
        sectionKey === "2/3 Way Match"
      ) {
        updatedData = buildR1Payload(
          r1RowsBySection[sectionKey] || []
        );
      } else {
        // Normal scoring tables
        updatedData = buildUpdatedSectionData(
          sectionData,
          remarks,
          disagreements,
          sectionKey
        );
      }

      const response = await fetch("/api/update-remark/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: detail.key,
          uniqueKey: detail.uniqueKey,
          sectionKey,
          role: "processor",
          updated_data: updatedData,

          dispose_record_ids:
            detail.key === "r1" &&
            sectionKey === "2/3 Way Match"
              ? disposeRecordIds
              : [],

          unallocated_cost_data:
            detail.key === "r1" &&
            sectionKey === "2/3 Way Match"
              ? unallocatedCostData
              : null,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Unable to save remarks."
        );
      }

      window.alert(
        result?.message || "Remarks saved successfully."
      );

      onSaved?.();
      onClose();
    } catch (requestError) {
      setError(
        requestError.message ||
          "Unable to save remarks."
      );
    } finally {
      setSavingSection("");
    }
  }

  function renderSection(sectionKey, sectionValue) {
    const parsedSectionValue = parseRawData(sectionValue);

    const sectionData =
      parsedSectionValue?.data ?? parsedSectionValue;

    

    const hasRemarkField = (() => {
      if (Array.isArray(sectionData)) {
        return sectionData.some((row) => Object.prototype.hasOwnProperty.call(row || {}, "processor_remark"));
      }
      if (isNestedObjectData(sectionData)) {
        return Object.values(sectionData).some((row) =>
          Object.prototype.hasOwnProperty.call(row || {}, "processor_remark"),
        );
      }
      return Boolean(sectionData && typeof sectionData === "object" && "processor_remark" in sectionData);
    })();

    const hasDisagreement = Object.entries(disagreements).some(
      ([compositeKey, value]) => {
        const [disagreementSectionKey] = compositeKey.split("::");

        return (
          disagreementSectionKey === (sectionKey || "default") &&
          value === true
        );
      }
    );

    const shouldShowSaveAction =
      hasRemarkField ||
      hasDisagreement ||
      (detail.key === "r1" && sectionKey === "2/3 Way Match");
    
    const content = (
      <>
        {sectionKey && !zoomedSection ? (
          <div className="invoice-check-modal__section-header">
            <h3 className="invoice-check-modal__section-title">
              {sectionKey}
            </h3>
            
            <span className="invoice-check-modal__section-score">
              Table Reliability:{" "}
              {invoiceRow?.averageTableScores?.[sectionKey] != null
                ? Number(invoiceRow.averageTableScores[sectionKey]).toFixed(2)
                : "—"}
            </span>
          </div>
        ) : null}
        <SectionTable
          data={sectionData}
          keyName={detail.key}
          sectionKey={sectionKey}
          remarks={remarks}
          onRemarkChange={handleRemarkChange}
          disagreements={disagreements}
          onDisagreeChange={handleDisagreeChange}
          scoringOptions={scoringOptions}
          r1Rows={r1RowsBySection[sectionKey]}
          // isZoomed={zoomedSection === sectionKey}
          isZoomed={detail.key === "r1" && zoomedSection === sectionKey}
          onR1DragStart={handleR1DragStart}
          onR1DragOver={(event, rowIndex) => {
            if (event) {
              event.preventDefault();
            }
            handleR1DragOver(sectionKey, rowIndex);
          }}
          onR1Drop={(event, rowIndex) => handleR1Drop(event, sectionKey, rowIndex)}
          onR1Break={(rowIndex) => handleR1Break(sectionKey, rowIndex)}
          onR1Zoom={() => setZoomedSection(sectionKey)}
          disposedRecordIds={disposeRecordIds}
          onR1Action={handleR1Action}
        />
        {shouldShowSaveAction ? (
          <div className="invoice-check-modal__actions">
            <button
              className="primary-button"
              type="button"
              disabled={savingSection === (sectionKey || "default")}
              onClick={() => handleSave(sectionKey, sectionData)}
            >
              {savingSection === (sectionKey || "default") ? "Saving..." : "Save Remark"}
            </button>
          </div>
        ) : null}
      </>
    );

    
    // const isZoomed = zoomedSection === sectionKey;
    const isZoomed = detail.key === "r1" && zoomedSection === sectionKey;

    return isZoomed ? (
      <div className="invoice-check-modal__zoom-shell">
        <div className="invoice-check-modal__zoom-header">
          <div className="invoice-check-modal__zoom-title">
            <strong>{sectionKey}</strong>

            <span className="invoice-check-modal__section-score">
              Table Reliability:{" "}
              {invoiceRow?.averageTableScores?.[sectionKey] != null
                ? Number(invoiceRow.averageTableScores[sectionKey]).toFixed(2)
                : "—"}
            </span>
          </div>

          <button
            className="ghost-button"
            type="button"
            onClick={() => setZoomedSection("")}
          >
            Exit Zoom
          </button>
        </div>

        {content}
      </div>
    ) : (
      content
    );

    return content;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className={`processor-modal__card processor-modal__card--wide invoice-check-modal ${
          zoomedSection ? "invoice-check-modal--zoomed" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-check-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        {!zoomedSection && (
          <div className="processor-modal__header">
            <div>
              <p className="eyebrow">Status Detail</p>
              <h2 id="invoice-check-detail-title">
                {detail.message || detail.label}
              </h2>
            </div>

            <button
              className="ghost-button"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        )}

        {!zoomedSection &&
        detail.message &&
        detail.message !== detail.label ? (
          <p className="config-note">
            {detail.message}
          </p>
        ) : null}

        {error ? <div className="config-banner config-banner--error">{error}</div> : null}

        {isEmptyData(parsedData) ? (
          <p className="empty-state">No data available for this check.</p>
        ) : isMultiSection ? (
          <div className="invoice-check-modal__sections">
            {Object.entries(parsedData)
              .filter(([sectionKey]) => !zoomedSection || sectionKey === zoomedSection)
              .map(([sectionKey, sectionValue]) => (
                <section key={sectionKey} className="invoice-check-modal__section">
                  {renderSection(sectionKey, sectionValue)}
                </section>
              ))}
          </div>
        ) : (
          <section className="invoice-check-modal__section">{renderSection("", parsedData)}</section>
        )}
      </div>
      {showUnallocatedModal &&
        createPortal(
          <div
            className="unallocated-modal-overlay"
            onClick={() => setShowUnallocatedModal(false)}
          >
            <div
              className="unallocated-modal"
              onClick={(event) => event.stopPropagation()}
            >
              {/* HEADER */}
              <div className="unallocated-modal__header">
                <div>
                  <h2>Unallocated Cost</h2>
                  <p>Add unallocated cost details for this invoice item</p>
                </div>

                <button
                  type="button"
                  className="unallocated-modal__close"
                  onClick={() => {
                    setShowUnallocatedModal(false);
                    setLineItemValue(null);
                  }}
                >
                  ×
                </button>
              </div>

              {/* BODY */}
              <div className="unallocated-modal__body">
                {(() => {
                  const invoiceValue = Number(
                    invoiceRow.invoiceValue || 0
                  );

                  const invoiceValueMigo = Number(
                    invoiceRow.invoiceMigoValue || 0
                  );
                  // console.log("invoiceValue",invoiceValue);
                  // console.log("invoiceValueMigo",invoiceValueMigo);
                  // console.log("LineItemValue",lineItemvalue);
                  
                  const existingUnallocatedCost = Number(
                    invoiceRow?.unallocatedCostValue || 0
                  );

                  const availableDifference =
                    invoiceValue -
                    invoiceValueMigo -
                    existingUnallocatedCost;

                  const processorConfig =
                    unplannedForm?.unallocated_cost_config?.processor || {};

                  const thresholdAmount = Number(
                    processorConfig.threshold_amount || 0
                  );

                  const thresholdPercent = Number(
                    processorConfig.threshold_percent || 0
                  );

                  const thresholdPercentValue =
                    (invoiceValue * thresholdPercent) / 100;

                  let configuredMaxAmount = 0;

                  if (processorConfig.condition === "OR") {
                    configuredMaxAmount = Math.min(
                      thresholdAmount,
                      thresholdPercentValue
                    );
                  } else {
                    configuredMaxAmount = Math.max(
                      thresholdAmount,
                      thresholdPercentValue
                    );
                  }

                  const _maxAllowedAmount = Math.min(
                    configuredMaxAmount,
                    availableDifference
                  );
                  const maxAllowedAmount = Math.min(
                    _maxAllowedAmount,
                    lineItemvalue
                  );

                  const enteredAmount = Number(
                    unplannedValues.amount || 0
                  );

                  const thresholdConfigured =
                    thresholdAmount > 0 ||
                    thresholdPercent > 0;

                  const canCreate =
                    availableDifference > 0 &&
                    thresholdConfigured &&
                    maxAllowedAmount > 0;

                  return (
                    <>
                      {/* SUMMARY */}
                      <div className="unallocated-modal__summary">
                        <div className="unallocated-summary-card">
                          <span>Invoice Value (OCR)</span>
                          <strong>{invoiceValue}</strong>
                        </div>

                        <div className="unallocated-summary-card">
                          <span>Invoice Value (MIGO)</span>
                          <strong>{invoiceValueMigo}</strong>
                        </div>

                        <div className="unallocated-summary-card">
                          <span>Existing Unallocated Cost</span>
                          <strong>{existingUnallocatedCost}</strong>
                        </div>

                        <div className="unallocated-summary-card">
                          <span>Available Difference</span>
                          <strong>{availableDifference}</strong>
                        </div>
                      </div>

                      {/* MAXIMUM ALLOWED */}
                      {canCreate && (
                        <div className="unallocated-modal__info">
                          Maximum Allowed Amount:
                          <strong>{maxAllowedAmount}</strong>
                        </div>
                      )}

                      {!thresholdConfigured && (
                        <div className="unallocated-modal__warning">
                          No threshold configured for Processor.
                        </div>
                      )}

                      {availableDifference <= 0 && (
                        <div className="unallocated-modal__warning">
                          Invoice Value must be greater than Invoice Value (MIGO)
                          before Unallocated Cost can be created.
                        </div>
                      )}

                      {canCreate && (
                        <>
                          {/* BASIC DETAILS */}
                          <div className="unallocated-section">
                            <h3>Unallocated Cost Details</h3>

                            <div className="unallocated-form-grid">
                              {/* AMOUNT */}
                              <label>
                                <span>Amount</span>

                                <input
                                  type="number"
                                  min="0"
                                  max={maxAllowedAmount}
                                  value={unplannedValues.amount || ""}
                                  onChange={(event) => {
                                    const value = event.target.value;

                                    if (value === "") {
                                      updateUnplannedField(
                                        "amount",
                                        ""
                                      );
                                      return;
                                    }

                                    const numericValue = Math.min(
                                      Number(value),
                                      maxAllowedAmount
                                    );

                                    updateUnplannedField(
                                      "amount",
                                      numericValue
                                    );
                                  }}
                                />
                              </label>

                              {/* DESCRIPTION */}
                              <label>
                                <span>Description</span>

                                <input
                                  type="text"
                                  value={
                                    unplannedValues.item_descritption || ""
                                  }
                                  onChange={(event) =>
                                    updateUnplannedField(
                                      "item_descritption",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>

                              {/* TAX CODE */}
                              <label>
                                <span>Tax Code</span>

                                <select
                                  value={
                                    unplannedValues.TaxCode || ""
                                  }
                                  onChange={(event) =>
                                    updateUnplannedField(
                                      "TaxCode",
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="">
                                    Select Tax Code
                                  </option>

                                  {(unplannedForm?.gst_taxcode_list || []).map(
                                    (code) => (
                                      <option
                                        key={code}
                                        value={code}
                                      >
                                        {code}
                                      </option>
                                    )
                                  )}
                                </select>
                              </label>

                              {/* WITHHOLDING TAX */}
                              <label>
                                <span>Withholding Tax Code</span>

                                <select
                                  value={
                                    unplannedValues.WithholdingTaxCode || ""
                                  }
                                  onChange={(event) =>
                                    updateUnplannedField(
                                      "WithholdingTaxCode",
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="">
                                    Select Withholding Tax
                                  </option>

                                  {(unplannedForm?.wtaxcode_list || []).map(
                                    (code) => (
                                      <option
                                        key={code}
                                        value={code}
                                      >
                                        {code}
                                      </option>
                                    )
                                  )}
                                </select>
                              </label>

                              {/* GL ACCOUNT */}
                              {unplannedForm?.unallocated_cost_config
                                ?.add_to_material_service === "Y" ? (
                                <label>
                                  <span>GL Account</span>

                                  <input
                                    type="text"
                                    value={
                                      unplannedValues.GLAccount || ""
                                    }
                                    readOnly
                                  />
                                </label>
                              ) : (
                                <label>
                                  <span>GL Account</span>

                                  <select
                                    value={
                                      unplannedValues.GLAccount || ""
                                    }
                                    onChange={(event) =>
                                      updateUnplannedField(
                                        "GLAccount",
                                        event.target.value
                                      )
                                    }
                                  >
                                    <option value="">
                                      Select GL Code
                                    </option>

                                    {(unplannedForm?.gl_list || []).map(
                                      (gl) => (
                                        <option
                                          key={gl.code}
                                          value={gl.code}
                                        >
                                          {gl.description
                                            ? `${gl.code} (${gl.description})`
                                            : gl.code}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </label>
                              )}
                            </div>
                          </div>

                          {/* GRN */}
                          {Object.keys(unplannedForm?.grn_fields || {}).length >
                            0 && (
                            <div className="unallocated-section">
                              <h3>GRN Information</h3>

                              <div className="unallocated-form-grid">
                                {Object.values(
                                  unplannedForm?.grn_fields || {}
                                ).map((field) => (
                                  <label key={`grn-${field.field}`}>
                                    <span>{field.field}</span>

                                    <input
                                      type="text"
                                      value={
                                        unplannedValues[field.field] || ""
                                      }
                                      onChange={(event) =>
                                        updateUnplannedField(
                                          field.field,
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* PO */}
                          {Object.keys(unplannedForm?.po_fields || {}).length >
                            0 && (
                            <div className="unallocated-section">
                              <h3>PO Information</h3>

                              <div className="unallocated-form-grid">
                                {Object.values(
                                  unplannedForm?.po_fields || {}
                                ).map((field) => (
                                  <label key={`po-${field.field}`}>
                                    <span>{field.field}</span>

                                    <input
                                      type="text"
                                      value={
                                        unplannedValues[field.field] || ""
                                      }
                                      onChange={(event) =>
                                        updateUnplannedField(
                                          field.field,
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* VALIDATION */}
                          {enteredAmount > maxAllowedAmount && (
                            <div className="unallocated-modal__warning">
                              Maximum allowed amount is {maxAllowedAmount}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* FOOTER */}
              <div className="unallocated-modal__footer">
                <button
                  type="button"
                  className="unallocated-btn unallocated-btn--secondary"
                  onClick={() => setShowUnallocatedModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="unallocated-btn unallocated-btn--primary"
                  onClick={handleUnallocatedCostSave}
                >
                  Save Unallocated Cost
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
    
  );
}
